import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { trackSignUp } from "@/lib/gtm";
import { trackCompleteRegistration } from "@/lib/metaPixel";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Lock, Check, X as XIcon } from "lucide-react";
import { TERMS_VERSION, PRIVACY_VERSION } from "@/pages/Auth";
import { evaluatePasswordStrength } from "@/lib/passwordStrength";

// Landing page for a parent-invite link (/invite/:token). Shows who invited
// the user, then collects email + password only (no payment). On success the
// backend auto-logs them in and we forward them to the main parent dashboard.
export default function InviteAccept() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ state: "loading" });
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/parent-invite/lookup/${encodeURIComponent(token)}`)
      .then((r) => setStatus({ state: "ok", primary_name: r.data.primary_name, primary_email: r.data.primary_email }))
      .catch((e) => setStatus({ state: "error", message: errMsg(e, "This invite link is not valid.") }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    if (!agreeTerms || !agreePrivacy) {
      toast.error("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/parent-invite/accept/${encodeURIComponent(token)}`, {
        email, password, first_name: firstName.trim(), surname: lastName.trim(),
        terms_version: TERMS_VERSION, privacy_version: PRIVACY_VERSION,
      });
      trackSignUp("parent", "parent_invite");
      trackCompleteRegistration("second_parent");
      await refresh();
      toast.success("You're linked! Welcome to Compass Kids.");
      navigate(data.next_path || "/dashboard", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Could not accept this invite."));
    } finally {
      setSubmitting(false);
    }
  };

  if (status.state === "loading") {
    return <div className="max-w-md mx-auto px-4 py-16 text-center text-forest/60" data-testid="invite-loading">Checking invite...</div>;
  }
  if (status.state === "error") {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="invite-error">
        <p className="text-forest/80 font-bold">{status.message}</p>
        <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/")} data-testid="invite-error-home">Back to home</Button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="invite-accept-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-5 h-5 text-sage-dark" />
          <p className="text-xs font-bold uppercase tracking-wide text-forest/60">Parent invite</p>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-forest mb-2" data-testid="invite-heading">
          Join {status.primary_name || "the family profile"}
        </h1>
        <p className="text-sm text-forest/60 mb-6" data-testid="invite-primary-email">
          You've been invited by {status.primary_name || status.primary_email}. Set your email and password below — no payment is required, you'll share the family subscription.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="invite-email" className="text-forest/80">Email Address</Label>
            <Input id="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="invite-email-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="invite-first-name" className="text-forest/80">First Name</Label>
              <Input id="invite-first-name" required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="invite-first-name-input" />
            </div>
            <div>
              <Label htmlFor="invite-last-name" className="text-forest/80">Last Name</Label>
              <Input id="invite-last-name" required value={lastName} onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="invite-last-name-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="invite-password" className="text-forest/80">Password</Label>
            <Input id="invite-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="invite-password-input" />
            {password && (() => {
              const s = evaluatePasswordStrength(password);
              return (
                <div className="mt-1.5" data-testid="invite-password-strength">
                  <div className="h-1.5 w-full bg-sage-light/40 rounded-full overflow-hidden">
                    <div className={`h-full ${s.color} transition-all`} style={{ width: `${s.score}%` }} data-testid="invite-password-strength-bar" />
                  </div>
                  <p className="text-[11px] text-forest/60 mt-1" data-testid="invite-password-strength-label">Strength: <span className="font-bold">{s.label}</span></p>
                </div>
              );
            })()}
          </div>
          <div>
            <Label htmlFor="invite-confirm" className="text-forest/80">Confirm password</Label>
            <Input id="invite-confirm" type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Type the password again" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="invite-confirm-input" />
            {confirmPassword && (password === confirmPassword ? (
              <p className="text-[11px] text-sage-dark mt-1 flex items-center gap-1" data-testid="invite-password-match">
                <Check className="w-3 h-3" /> Passwords match
              </p>
            ) : (
              <p className="text-[11px] text-terracotta-dark mt-1 flex items-center gap-1" data-testid="invite-password-mismatch">
                <XIcon className="w-3 h-3" /> Passwords do not match
              </p>
            ))}
          </div>

          <div className="space-y-3 rounded-xl bg-terracotta-light/60 border-2 border-terracotta/40 p-4" data-testid="invite-consent-block">
            <p className="text-xs font-bold text-terracotta-dark uppercase tracking-wide" data-testid="invite-consent-header">
              Required — please tick both to continue
            </p>
            <label className="flex items-start gap-3 text-sm text-forest cursor-pointer select-none" data-testid="invite-agree-terms-label">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                data-testid="invite-agree-terms-checkbox"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-terracotta-dark underline" data-testid="invite-terms-link">
                  Terms &amp; Conditions
                </Link>
                .
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-forest cursor-pointer select-none" data-testid="invite-agree-privacy-label">
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                data-testid="invite-agree-privacy-checkbox"
              />
              <span>
                I agree to the{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-bold text-terracotta-dark underline" data-testid="invite-privacy-link">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
          </div>

          <Button type="submit" disabled={submitting || !email || !firstName.trim() || !lastName.trim() || !password || !confirmPassword || !agreeTerms || !agreePrivacy}
            className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md" data-testid="invite-submit-btn">
            <Lock className="w-4 h-4 mr-2" /> {submitting ? "Linking..." : "Create account & link"}
          </Button>
          {(!agreeTerms || !agreePrivacy) && (
            <p className="text-[11px] text-center text-terracotta-dark" data-testid="invite-consent-hint">
              Tick both boxes above to enable Sign Up.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
